import { useContext, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { CurrencyContext } from '../contexts/CurrencyContext';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { palette, typography, theme } from '../theme/colors';

export const CurrencySelector = () => {
  const { currency, setCurrency, supportedCurrencies, currencySymbols, currencyNames } = useContext(CurrencyContext);
  const { mode } = useContext(ColorModeContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    surfaceHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency as any);
    handleMenuClose();
  };

  return (
    <>
      <Tooltip title={`CURRENCY: ${currency}`}>
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            py: 0.5,
            px: 1.25,
            borderRadius: '8px',
            transition: theme.transitions.fast,
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: alpha(colors.textSecondary, 0.08),
              color: colors.textPrimary,
            },
          }}
        >
          <MonetizationOnOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography
            variant="caption"
            sx={{
              color: 'inherit',
              fontFamily: typography.fontFamily.mono,
              fontSize: '12px',
              fontWeight: typography.fontWeight.semibold,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {currency}
          </Typography>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surfaceElevated,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {/* Menu Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              fontFamily: typography.fontFamily.body,
              fontSize: '13px',
            }}
          >
            Select Currency
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.textMuted,
              display: 'block',
              fontSize: '11px',
              fontFamily: typography.fontFamily.body,
            }}
          >
            Prices will be displayed in {currencyNames[currency]}
          </Typography>
        </Box>

        {/* Currency Options */}
        <Box sx={{ py: 0.5 }}>
          {supportedCurrencies.map((curr) => (
            <MenuItem
              key={curr}
              onClick={() => handleCurrencyChange(curr)}
              selected={currency === curr}
              sx={{
                py: 1,
                px: 2,
                borderRadius: '4px',
                mx: 0.5,
                '&:hover': {
                  backgroundColor: alpha(colors.textSecondary, 0.08),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(palette.primary, 0.08),
                  '&:hover': {
                    backgroundColor: alpha(palette.primary, 0.12),
                  },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: typography.fontWeight.semibold,
                      fontSize: '16px',
                      color: colors.textPrimary,
                      minWidth: 32,
                    }}
                  >
                    {currencySymbols[curr]}
                  </Typography>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textPrimary,
                        fontFamily: typography.fontFamily.body,
                        fontWeight: typography.fontWeight.medium,
                        fontSize: '13px',
                      }}
                    >
                      {currencyNames[curr]}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: '11px',
                        fontWeight: typography.fontWeight.semibold,
                      }}
                    >
                      {curr}
                    </Typography>
                  </Box>
                </Box>
                {currency === curr && (
                  <CheckIcon
                    sx={{
                      fontSize: 16,
                      color: palette.primary,
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </>
  );
};
